import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import routes from 'routes';

import Footer from 'components/common/Footer';
import Header from 'components/admin/Header';
import AdminMenu from 'components/navigation/AdminMenu';
import Loading from 'components/common/Loading';

import useEnsureClubs, { ClubStatus } from 'hooks/useEnsureClubs';
import { getToken } from 'services/auth/tokenService';
import { decodeToken } from 'utils/auth';

const AdminLayout = () => {
  const navigate = useNavigate();

  const { clubs, selectedClub } = useSelector((state) => state.user || {});

  // Club membership is resolved once, in one shared place (see useEnsureClubs).
  const status = useEnsureClubs();
  const menuRef = useRef(null);

  const [isOpenedMenu, setIsOpenedMenu] = useState(false);

  // A platform citizen with no active club doesn't belong in the club shell.
  useEffect(() => {
    if (status !== ClubStatus.NoClubs) {
      return;
    }
    const token = getToken();
    const portalRole = token ? decodeToken(token)?.userRole : null;
    if (portalRole === 'AccountOwner') {
      navigate(routes.auth.setupClub);
    } else {
      // RegisteredUser with no active clubs → their personal portal.
      navigate(routes.personal.home);
    }
  }, [status, navigate]);

  useEffect(() => {
    if (isOpenedMenu) {
      const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          setIsOpenedMenu(false); // Close the menu if the click is outside
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpenedMenu]);

  const openMenu = () => {
    setIsOpenedMenu(true);
  };

  const closeMenu = () => {
    setIsOpenedMenu(false);
  };

  // Still resolving, or about to be redirected out of the club shell.
  if (status !== ClubStatus.HasClubs) {
    return <Loading />;
  }

  return (
    <>
      <Header openMenu={openMenu} club={selectedClub} />
      <Outlet />
      <div ref={menuRef}>
        <AdminMenu
          isOpened={isOpenedMenu}
          closeMenu={closeMenu}
          selectedClub={selectedClub}
          clubs={clubs}
        />
      </div>
      <Footer />
    </>
  );
};

export default AdminLayout;
